const MAX_LENGTH = 31;
const CAMERA_ID_PATTERN = /^[a-zA-Z0-9_+-]+$/;

declare const CameraIdBrand: unique symbol;

/**
 * A unique identifier for a Camera.
 *
 * Structure:
 * - A string with characters [a-zA-Z0-9_+-] (1-31 chars)
 */
export type CameraId = string & { readonly [CameraIdBrand]: "CameraId" };

export class CameraIdError extends Error {
  constructor(
    public readonly kind: "OutOfRange" | "Format",
    message: string,
  ) {
    super(message);
  }
}

/**
 * Constructs a CameraId from a string or throws a CameraIdError.
 *
 * @param str - A string (1-31 chars, [a-zA-Z0-9_+-])
 */
const fromString = (str: string): CameraId => {
  if (typeof str !== "string" || str.length === 0) {
    throw new CameraIdError("OutOfRange", `Camera ID cannot be empty.`);
  }

  if (str.length > MAX_LENGTH) {
    throw new CameraIdError(
      "OutOfRange",
      `Camera ID ${str} exceeds maximum length. Expected at most ${String(MAX_LENGTH)} characters, got ${String(str.length)}.`,
    );
  }

  if (!CAMERA_ID_PATTERN.test(str)) {
    throw new CameraIdError("Format", `Camera ID ${str} contains invalid characters. Expected only [a-zA-Z0-9_+-].`);
  }

  return str as CameraId;
};

/**
 * Returns the string representation of a CameraId.
 */
const toString = (id: CameraId): string => {
  return id as string;
};

/**
 * Template tag for interpolating CameraId values in template literals.
 * Example: f`Camera${cameraId}Config` → "Cameramy-camera-123Config"
 */
const f = (strings: TemplateStringsArray, ...values: CameraId[]): string =>
  (strings[0] ?? "") + values.map((v, i) => toString(v) + (strings[i + 1] ?? "")).join("");

export const CameraId = {
  fromString,
  toString,
  f,
} as const;
